import { EstateOwner } from "@1games-ru/region-shared/dist/enums/estate/estate";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import EstateMpProvider from "@/core/data-provider/data-mp/estate/estate-mp-provider.ts";
import PlayerMpProvider from "@/core/data-provider/data-mp/player/player-mp-provider";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import { Tab } from "@/domains/estate/constants/tab";
import {
  EstateState,
  changeCurrentTenantId,
  currentTenantSelector,
  estateSelector,
  removeTenant,
  setCurrentTab,
  setTenant,
  tenantsSelector,
} from "@/domains/estate/slice";
import toast from "@/domains/toasts/helpers/toast";

export default () => {
  const { t } = useTranslation("estate");
  const [isRemoving, setIsRemoving] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const estate = useAppSelector(estateSelector);
  const tenants = useAppSelector(tenantsSelector);
  const currentTenant = useAppSelector(currentTenantSelector);
  const dispatch = useAppDispatch();

  const updateTenantHandler = useCallback(
    async (data: Partial<EstateState["tenants"]["data"][0]>) => {
      const { permissions } = data;
      if (currentTenant) {
        try {
          setIsUpdating(true);
          const response = await EstateMpProvider.updateTenant({
            characterId: currentTenant.characterId,
            estateId: estate?.id,
            fridge: permissions.fridge,
            garage: permissions.garage,
            safe: permissions.safe,
            upgrade: permissions.upgrade,
            wardrobe: permissions.wardrobe,
          });

          if (response instanceof RpcMpProviderSuccessResponse) {
            dispatch(setTenant({ id: currentTenant.characterId, data }));
          } else {
            console.error("update tenant error", response);
            toast.error(t("estate.errors.CANT_UPDATE_TENANT_ERROR"));
          }
        } catch (e) {
          console.log(e);
          toast.error(t("estate.errors.UPDATE_TENANT_SYSTEM_ERROR"));
        } finally {
          setIsUpdating(false);
        }
      }
    },
    [currentTenant, dispatch, estate?.id, t],
  );

  const removeCurrentTenantHandler = useCallback(async () => {
    if (currentTenant) {
      try {
        setIsRemoving(true);

        const response = await EstateMpProvider.removeTenant(estate?.id, currentTenant.characterId);

        if (response instanceof RpcMpProviderSuccessResponse) {
          dispatch(changeCurrentTenantId());
          dispatch(removeTenant(currentTenant.characterId));
        } else {
          console.error("remove tenant error", response);
          toast.error(t("estate.errors.CANT_REMOVE_TENANT_ERROR"));
        }
      } catch (e) {
        console.log(e);
        toast.error(t("estate.errors.REMOVE_TENANT_SYSTEM_ERROR"));
      } finally {
        setIsRemoving(false);
      }
    }
  }, [currentTenant, dispatch]);

  const changeCurrentTenantIdHandler = useCallback(
    async (id: number) => {
      const response = await PlayerMpProvider.getPlayer();
      if (response instanceof RpcMpProviderFailedResponse) {
        throw new Error("GetPlayer  error");
      }

      if (
        estate?.owner &&
        estate?.owner?.type === EstateOwner.Character &&
        estate?.owner.content.characterId !== response.data.character.id
      ) {
        toast.error(t("house.errors.HAS_NO_PERMISSION"));
        return;
      }

      dispatch(changeCurrentTenantId(id));
    },
    [dispatch, estate?.owner, t],
  );

  const sendEmailHandler = useCallback(() => {
    dispatch(setCurrentTab(Tab.Mail));
  }, [dispatch]);

  const backHandler = useCallback(() => {
    dispatch(changeCurrentTenantId());
  }, [dispatch]);

  return {
    tenants,
    currentTenant,
    changeCurrentTenantIdHandler,
    updateTenantHandler,
    removeCurrentTenantHandler,
    sendEmailHandler,
    backHandler,
    isUpdating,
    isRemoving,
  };
};
